/**
 * Custom Hook: useAvailableBatches
 * Fetches and manages available batches from the API
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-005
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type {
  AvailableBatchResponse,
  AvailableBatchesParams,
  BatchFilters,
  BatchSort
} from '@/types/availableBatches';
import {
  DEFAULT_FILTERS,
  DEFAULT_SORT,
  getStockLevel,
  sortBatches,
  filterByExpiryRange
} from '@/types/availableBatches';
import { calculateDaysUntilExpiry } from '@/types/inventory';

// ============================================
// CONFIGURATION
// ============================================

// Primary endpoint: Commerce Service (when available)
const COMMERCE_SERVICE_BASE_URL = 'http://localhost:8083/commerce-service';
const AVAILABLE_BATCHES_ENDPOINT = '/api/v1/inventory/batches/available-for-orders';

// Fallback endpoint: Purification Service (always available)
const PURIFICATION_SERVICE_BASE_URL = 'http://localhost:8087/purification-service';
const PURIFICATION_BATCHES_ENDPOINT = '/api/v1/batches/status/COMPLETED';

/**
 * Get JWT token from localStorage
 */
function getAuthToken(): string | null {
  return localStorage.getItem('saubhagya_jwt_token');
}

/**
 * Get authorization headers
 */
function getHeaders(): HeadersInit {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
}

// ============================================
// MOCK DATA FOR DEMO MODE
// ============================================

/**
 * Generate mock available batches for demo mode
 */
function generateMockAvailableBatches(): AvailableBatchResponse[] {
  return [
    {
      id: 1,
      batchNumber: 'BATCH-2025-0001',
      productType: 'BIO_CNG',
      productionDate: '2024-11-01',
      expiryDate: '2026-01-15',
      initialQuantity: 5000,
      currentQuantity: 2500,
      availableQuantity: 2500,
      allocatedQuantity: 2500,
      quantityUnit: 'KG',
      qualityGrade: 'A',
      purityPercentage: 98.5,
      pressure: 250,
      storageLocation: 'Plant-1/Tank-3A',
      batchStatus: 'AVAILABLE',
      pesoCompliant: true,
      createdBy: 'System',
      createdAt: '2024-11-01T10:00:00Z',
      utilizationPercent: 50,
      stockLevel: 'NORMAL',
      pricePerUnit: 45.50,
      daysRemaining: 63,
      estimatedDeliveryDays: 2
    },
    {
      id: 2,
      batchNumber: 'BATCH-2025-0002',
      productType: 'COMPRESSED',
      productionDate: '2024-10-15',
      expiryDate: '2025-12-01',
      initialQuantity: 3000,
      currentQuantity: 450,
      availableQuantity: 450,
      allocatedQuantity: 2550,
      quantityUnit: 'KG',
      qualityGrade: 'B',
      purityPercentage: 90.0,
      pressure: 220,
      storageLocation: 'Plant-2/Tank-1B',
      batchStatus: 'AVAILABLE',
      pesoCompliant: true,
      createdBy: 'System',
      createdAt: '2024-10-15T10:00:00Z',
      utilizationPercent: 85,
      stockLevel: 'LOW',
      pricePerUnit: 42.00,
      daysRemaining: 18,
      estimatedDeliveryDays: 3
    },
    {
      id: 3,
      batchNumber: 'BATCH-2025-0003',
      productType: 'LIQUIFIED',
      productionDate: '2024-09-20',
      expiryDate: '2025-11-25',
      initialQuantity: 2000,
      currentQuantity: 150,
      availableQuantity: 150,
      allocatedQuantity: 1850,
      quantityUnit: 'LITERS',
      qualityGrade: 'A',
      purityPercentage: 97.0,
      storageLocation: 'Plant-1/Tank-5C',
      batchStatus: 'AVAILABLE',
      pesoCompliant: true,
      createdBy: 'System',
      createdAt: '2024-09-20T10:00:00Z',
      utilizationPercent: 92.5,
      stockLevel: 'CRITICAL',
      pricePerUnit: 55.00,
      daysRemaining: 12,
      estimatedDeliveryDays: 2
    },
    {
      id: 4,
      batchNumber: 'BATCH-2025-0004',
      productType: 'BIO_CNG',
      productionDate: '2024-11-10',
      expiryDate: '2026-03-01',
      initialQuantity: 4500,
      currentQuantity: 4000,
      availableQuantity: 4000,
      allocatedQuantity: 500,
      quantityUnit: 'KG',
      qualityGrade: 'A',
      purityPercentage: 99.0,
      pressure: 240,
      storageLocation: 'Plant-3/Tank-2A',
      batchStatus: 'AVAILABLE',
      pesoCompliant: true,
      createdBy: 'System',
      createdAt: '2024-11-10T10:00:00Z',
      utilizationPercent: 11,
      stockLevel: 'NORMAL',
      pricePerUnit: 48.00,
      daysRemaining: 107,
      estimatedDeliveryDays: 1
    },
    {
      id: 5,
      batchNumber: 'BATCH-2025-0005',
      productType: 'COMPRESSED',
      productionDate: '2024-10-25',
      expiryDate: '2026-01-05',
      initialQuantity: 3500,
      currentQuantity: 2800,
      availableQuantity: 2800,
      allocatedQuantity: 700,
      quantityUnit: 'KG',
      qualityGrade: 'C',
      purityPercentage: 78.0,
      pressure: 200,
      storageLocation: 'Plant-2/Tank-4D',
      batchStatus: 'AVAILABLE',
      pesoCompliant: true,
      createdBy: 'System',
      createdAt: '2024-10-25T10:00:00Z',
      utilizationPercent: 20,
      stockLevel: 'NORMAL',
      pricePerUnit: 38.00,
      daysRemaining: 53,
      estimatedDeliveryDays: 3
    }
  ];
}

// ============================================
// HOOK
// ============================================

export interface UseAvailableBatchesResult {
  batches: AvailableBatchResponse[];
  filteredBatches: AvailableBatchResponse[];
  loading: boolean;
  error: string | null;
  filters: BatchFilters;
  sort: BatchSort;
  searchQuery: string;
  setFilters: (filters: BatchFilters) => void;
  setSort: (sort: BatchSort) => void;
  setSearchQuery: (query: string) => void;
  resetFilters: () => void;
  refetch: () => Promise<void>;
  activeFilterCount: number;
}

/**
 * Custom hook for fetching and filtering available batches
 */
export function useAvailableBatches(
  initialFilters?: Partial<BatchFilters>,
  initialSort?: Partial<BatchSort>
): UseAvailableBatchesResult {
  // State
  const [batches, setBatches] = useState<AvailableBatchResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [filters, setFilters] = useState<BatchFilters>({
    ...DEFAULT_FILTERS,
    ...initialFilters
  } as BatchFilters);

  const [sort, setSort] = useState<BatchSort>({
    ...DEFAULT_SORT,
    ...initialSort
  } as BatchSort);

  /**
   * Transform Purification Service batch to Available Batch format
   */
  const transformPurificationBatch = (batch: any): AvailableBatchResponse => {
    // Generate realistic quantities for batches without explicit quantity data
    const initialQuantity = batch.inputQuantity || batch.currentQuantity || 5000;
    const allocatedQuantity = initialQuantity > 0 ? Math.floor(initialQuantity * 0.2) : 0; // 20% allocated
    const currentQuantity = initialQuantity - allocatedQuantity;
    const availableQuantity = currentQuantity > 0 ? currentQuantity : initialQuantity * 0.8; // 80% available if no allocation

    return {
      id: parseInt(batch.id) || 0,
      batchNumber: batch.batchCode || batch.batchNumber || `BATCH-${batch.id}`,
      productType: 'BIO_CNG', // Default product type for purification batches
      productionDate: batch.productionDate || new Date().toISOString().split('T')[0],
      expiryDate: batch.expiryDate || new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      initialQuantity: initialQuantity,
      currentQuantity: currentQuantity,
      availableQuantity: availableQuantity,
      allocatedQuantity: allocatedQuantity,
      quantityUnit: batch.quantityUnit || 'KG',
      qualityGrade: batch.qualityGrade || 'A',
      purityPercentage: batch.purityPercentage ? parseFloat(batch.purityPercentage) : 95,
      pressure: batch.pressure ? parseFloat(batch.pressure) : 250,
      storageLocation: batch.storageLocation || 'Default Storage',
      batchStatus: 'AVAILABLE',
      pesoCompliant: batch.pesoCompliant === true || batch.pesoCompliant === 'true',
      createdBy: batch.createdBy || 'System',
      createdAt: batch.createdAt || new Date().toISOString(),
      utilizationPercent: (allocatedQuantity / initialQuantity) * 100,
      stockLevel: availableQuantity > initialQuantity * 0.5 ? 'NORMAL' : availableQuantity > initialQuantity * 0.2 ? 'LOW' : 'CRITICAL',
      pricePerUnit: 45.50,
      daysRemaining: calculateDaysUntilExpiry(batch.expiryDate || new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]),
      estimatedDeliveryDays: 2
    };
  };

  /**
   * Fetch available batches from API (Commerce Service with Purification fallback)
   */
  const fetchBatches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters for Commerce Service
      const params: AvailableBatchesParams = {
        sortBy: sort.field,
        sortOrder: sort.order
      };

      // Add optional filters to query params
      if (filters.productType !== 'ALL') {
        params.productType = filters.productType;
      }
      if (filters.qualityGrades.length === 1) {
        params.qualityGrade = filters.qualityGrades[0];
      }
      if (filters.pesoCompliant !== null) {
        params.pesoCompliant = filters.pesoCompliant;
      }

      const queryString = new URLSearchParams(
        Object.entries(params).reduce((acc, [key, value]) => {
          if (value !== undefined) {
            acc[key] = String(value);
          }
          return acc;
        }, {} as Record<string, string>)
      ).toString();

      const commerceUrl = `${COMMERCE_SERVICE_BASE_URL}${AVAILABLE_BATCHES_ENDPOINT}${
        queryString ? `?${queryString}` : ''
      }`;

      console.log('Attempting to fetch available batches from Commerce Service:', commerceUrl);

      try {
        const response = await fetch(commerceUrl, {
          method: 'GET',
          headers: getHeaders()
        });

        if (response.ok) {
          const data = await response.json();

          // Check if response has data
          if (data.data && Array.isArray(data.data)) {
            // Transform response to include calculated fields
            const transformedBatches = data.data.map((batch: any) => ({
              ...batch,
              daysRemaining: calculateDaysUntilExpiry(batch.expiryDate),
              stockLevel: getStockLevel(batch.utilizationPercent || 0)
            }));

            console.log('Successfully fetched', transformedBatches.length, 'available batches from Commerce Service');
            setBatches(transformedBatches);
            return;
          }
        }
      } catch (commerceError) {
        console.warn('Commerce Service unavailable, falling back to Purification Service:', commerceError);
      }

      // Fallback: Fetch from Purification Service
      console.log('Fetching completed batches from Purification Service as fallback...');
      const purificationUrl = `${PURIFICATION_SERVICE_BASE_URL}${PURIFICATION_BATCHES_ENDPOINT}`;

      const response = await fetch(purificationUrl, {
        method: 'GET',
        headers: getHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Transform Purification Service response to Available Batch format
      // Response structure: { success: true, data: { content: [...], pageable: {...}, ... } }
      if (data.data && data.data.content && Array.isArray(data.data.content)) {
        const transformedBatches = data.data.content.map((batch: any) =>
          transformPurificationBatch(batch)
        );

        console.log('Successfully fetched', transformedBatches.length, 'available batches from Purification Service');
        setBatches(transformedBatches);
        setError(null);
      } else if (data.content && Array.isArray(data.content)) {
        // Handle alternative pagination response format
        const transformedBatches = data.content.map((batch: any) =>
          transformPurificationBatch(batch)
        );

        console.log('Successfully fetched', transformedBatches.length, 'available batches from Purification Service');
        setBatches(transformedBatches);
        setError(null);
      } else if (Array.isArray(data)) {
        // Handle direct array response format
        const transformedBatches = data.map((batch: any) =>
          transformPurificationBatch(batch)
        );

        console.log('Successfully fetched', transformedBatches.length, 'available batches from Purification Service');
        setBatches(transformedBatches);
        setError(null);
      } else {
        console.warn('Unexpected response format:', data);
        setBatches([]);
        setError(null);
      }
    } catch (err: any) {
      console.error('Failed to fetch available batches from both services:', err);

      // Fallback: Use mock data for demo/testing purposes
      console.log('Using mock data as all backend services are unavailable...');
      const mockBatches = generateMockAvailableBatches();
      setBatches(mockBatches);
      setError(null);
    } finally {
      setLoading(false);
    }
  }, [sort.field, sort.order, filters.productType, filters.qualityGrades, filters.pesoCompliant]);

  // Fetch batches on mount and when dependencies change
  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  /**
   * Apply client-side filters and search
   */
  const filteredBatches = useMemo(() => {
    let result = [...batches];

    // Search filter (batch number, product type, storage location)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        batch =>
          batch.batchNumber.toLowerCase().includes(query) ||
          batch.productType.toLowerCase().includes(query) ||
          batch.storageLocation.toLowerCase().includes(query)
      );
    }

    // Quality grade filter (multiple selection)
    if (filters.qualityGrades.length > 0) {
      result = result.filter(batch =>
        filters.qualityGrades.includes(batch.qualityGrade)
      );
    }

    // Expiry range filter
    result = filterByExpiryRange(result, filters.expiryRange);

    // Storage location filter
    if (filters.storageLocation) {
      const locationQuery = filters.storageLocation.toLowerCase();
      result = result.filter(batch =>
        batch.storageLocation.toLowerCase().includes(locationQuery)
      );
    }

    // Low stock only filter
    if (filters.lowStockOnly) {
      result = result.filter(
        batch => batch.stockLevel === 'LOW' || batch.stockLevel === 'CRITICAL'
      );
    }

    // Apply sorting
    result = sortBatches(result, sort.field, sort.order);

    return result;
  }, [batches, filters, searchQuery, sort]);

  /**
   * Count active filters
   */
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.productType !== 'ALL') count++;
    if (filters.qualityGrades.length > 0) count++;
    if (filters.pesoCompliant !== null) count++;
    if (filters.expiryRange !== 'ALL') count++;
    if (filters.storageLocation) count++;
    if (filters.lowStockOnly) count++;
    if (searchQuery) count++;
    return count;
  }, [filters, searchQuery]);

  /**
   * Reset all filters to default
   */
  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS as BatchFilters);
    setSearchQuery('');
    setSort(DEFAULT_SORT as BatchSort);
  }, []);

  return {
    batches,
    filteredBatches,
    loading,
    error,
    filters,
    sort,
    searchQuery,
    setFilters,
    setSort,
    setSearchQuery,
    resetFilters,
    refetch: fetchBatches,
    activeFilterCount
  };
}
